import { LogOut, Moon, Sun } from "lucide-react";
import yn from "yn";
import { Avatar, AvatarFallback, AvatarImage } from "~/components/ui/avatar";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import useUser from "~/contexts/user";
import { cn } from "~/lib/utils";
import { zendeskUrl } from "~/utils/zendesk";

const { VITE_SUPPORT_EMAIL, VITE_HELP_CENTER_ENABLED } = import.meta.env;

interface ManifoldNextGenAccountMenuProps {
  collapsed: boolean;
  isDark: boolean;
  onToggleDark: () => void;
  onSwitchToClassic: () => void;
}

export function ManifoldNextGenAccountMenu({
  collapsed,
  isDark,
  onToggleDark,
  onSwitchToClassic,
}: ManifoldNextGenAccountMenuProps) {
  const user = useUser();
  const helpCenterEnabled = yn(VITE_HELP_CENTER_ENABLED, { default: true });
  const newTabProps = { target: "_blank", rel: "noopener noreferrer" };
  const initials =
    `${user?.given_name?.[0] ?? ""}${user?.family_name?.[0] ?? ""}` ||
    user?.email?.[0]?.toUpperCase();
  const displayName = user?.name ?? user?.email ?? "";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          aria-label="Account menu"
          className={cn(
            "w-full text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-0",
            collapsed ? "justify-center px-0" : "justify-start gap-2.5 px-3"
          )}
        >
          <Avatar className="size-7 shrink-0">
            <AvatarImage src={user?.picture} alt={displayName} />
            <AvatarFallback className="text-xs">{initials}</AvatarFallback>
          </Avatar>
          {!collapsed && <span className="min-w-0 truncate whitespace-nowrap">{displayName}</span>}
        </Button>
      </DropdownMenuTrigger>
      {/* TODO: revisit when we look into Radix UI components and how they can work with nextgen styles */}
      <DropdownMenuContent side="top" align="start" className="w-56" data-theme="manifold-nextgen">
        <DropdownMenuLabel className="flex flex-col gap-0.5">
          <span className="truncate font-medium">{displayName}</span>
          {user?.name && (
            <span className="text-muted-foreground truncate text-xs font-normal">{user.email}</span>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onToggleDark}>
          {isDark ? <Sun /> : <Moon />}
          {isDark ? "Light mode" : "Dark mode"}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onSwitchToClassic}>Switch to classic look</DropdownMenuItem>
        <DropdownMenuSeparator />
        {helpCenterEnabled && (
          <DropdownMenuItem asChild>
            <a href={zendeskUrl()} {...newTabProps}>
              Help Center
            </a>
          </DropdownMenuItem>
        )}
        <DropdownMenuItem asChild>
          <a
            href={VITE_SUPPORT_EMAIL ? `mailto:${VITE_SUPPORT_EMAIL}` : zendeskUrl("/requests/new")}
            {...(!VITE_SUPPORT_EMAIL ? newTabProps : {})}
          >
            Contact Support
          </a>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <a href="/logout">
            <LogOut />
            Sign Out
          </a>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
